import type { Pricing } from '@/types/checkout';
import { BillingFrequency, type Plan } from '@/types/subscription';
import { formatPriceWithCurrency } from '@/utils/pricing';

/**
 * Ported from independent-checkout-v2 (ACDEV-26078). ROSCA/FTC renewal
 * disclosure inputs — legal requirement, not cosmetic.
 */

export interface SubscriptionDisclaimerData {
  renewalPeriodKey: string;
  amount: string;
  taxesKey: string;
}

// The pay button must not render without this data (ROSCA/FTC), so the
// disclaimer and the button are gated on the same source.
export function getSubscriptionDisclaimerData(
  subscriptionData: { plan?: Plan; pricing?: Pricing } | undefined,
  isTaxExcludedCountry: boolean
): SubscriptionDisclaimerData | undefined {
  const renewalPeriodKey = getRenewalPeriodKey(subscriptionData?.plan?.billingFrequency);
  const priceBreakdown = subscriptionData?.pricing?.priceBreakdown;
  // Where tax is added on top of the price (US/CA) the renewal amount is quoted
  // pre-tax as "excluded taxes"; elsewhere tax is part of the total, quoted as
  // "included taxes".
  const renewalPrice = isTaxExcludedCountry ? priceBreakdown?.baseNet : priceBreakdown?.priceTotal;
  const pricing = subscriptionData?.pricing;
  const amount = pricing && renewalPrice !== undefined ? formatPriceWithCurrency(renewalPrice, pricing) : undefined;
  const taxesKey = isTaxExcludedCountry ? 'taxesExcluded' : 'taxesIncluded';

  return renewalPeriodKey && amount ? { renewalPeriodKey, amount, taxesKey } : undefined;
}

// Translation keys worded per the legal template (ROSCA/FTC): "renew [annually/monthly]".
const BILLING_FREQUENCY_TO_RENEWAL_KEY_MAP: Record<BillingFrequency, string> = {
  [BillingFrequency.DAILY]: 'renewalPeriodDaily',
  [BillingFrequency.WEEKLY]: 'renewalPeriodWeekly',
  [BillingFrequency.MONTHLY]: 'renewalPeriodMonthly',
  [BillingFrequency.YEARLY]: 'renewalPeriodAnnually',
};

export function getRenewalPeriodKey(frequency: BillingFrequency | undefined) {
  return frequency ? BILLING_FREQUENCY_TO_RENEWAL_KEY_MAP[frequency] : undefined;
}
